import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { trackConversion } from "@/lib/analytics";
import { recordGateSignup } from "@/lib/gateStats";
import { Flame, Loader2 } from "lucide-react";

export default function AuthCallback() {
  const { googleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams((location.hash || "").replace(/^#/, ""));
    const sessionId = params.get("session_id");

    // Clear the fragment so the session_id is used once and discarded.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    googleSession(sessionId)
      .then(({ user: u, isNew }) => {
        if (isNew) { trackConversion("sign_up", { method: "google", value: 1.0, currency: "USD" }); recordGateSignup("google"); }
        navigate(u?.onboarding?.required ? "/welcome" : "/dashboard", { replace: true });
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  }, [location.hash, googleSession, navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#0A192F] blueprint-grid p-6" data-testid="auth-callback">
      <div className="text-center max-w-sm relative z-10">
        <span className="inline-grid place-items-center w-12 h-12 rounded-md bg-primary mb-5"><Flame size={24} className="text-white" /></span>
        {error ? (
          <>
            <h1 className="font-heading text-2xl font-black mb-2">Sign-in failed</h1>
            <p data-testid="auth-callback-error" className="text-slate-400 text-sm mb-6">{error}</p>
            <button onClick={() => navigate("/login", { replace: true })}
              className="text-primary hover:underline font-medium text-sm" data-testid="auth-callback-retry">
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <Loader2 className="animate-spin text-primary" size={20} />
              <span className="text-sm">Completing Google sign-in…</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
